import { Fragment, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Layers, Search, Sparkles, X } from "lucide-react";
import Loader from "../../../common/Loader";
import StatusMessage from "./StatusMessage";
import { useDebounce } from "../hooks/useDebounce";
import { toTitleCase } from "../utils/normalizers";

// First N feature keys render as their own grouping columns (e.g. Segment,
// Collection, Product Category); the remaining keys collapse into "Features".
const GROUP_COLUMN_COUNT = 3;
const ITEMS_PER_PAGE = 30;
const PAGES_TO_SHOW = 5;

const getVisiblePages = (currentPage, totalPages) => {
  let startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(totalPages, startPage + PAGES_TO_SHOW - 1);

  if (endPage - startPage + 1 < PAGES_TO_SHOW) {
    startPage = Math.max(1, endPage - PAGES_TO_SHOW + 1);
  }

  return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
};

// `combinations` holds the FULL result set for the product type (the parent
// merges every backend page together), so search and pagination here run
// entirely client-side across all of it — not just one page at a time.
const FeatureCombinationsTable = ({
  combinations,
  productType,
  total,
  isLoading,
  isFetching,
  error,
  selectedCombinationIds,
  expandedCombinationIds,
  onToggleCombination,
  onToggleAllCombinations,
  onToggleDetails,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const debouncedSearch = useDebounce(searchTerm, 280);

  // Prefix the group number with the product type's initial (e.g. "S" for
  // Sanitaryware, "F" for Faucet) so group labels stay distinct across types.
  const groupPrefix = productType?.trim()?.charAt(0)?.toUpperCase() || "";

  const featureKeys = useMemo(() => {
    const withKeys = combinations.find((c) => c.combination && Object.keys(c.combination).length);
    return withKeys ? Object.keys(withKeys.combination) : [];
  }, [combinations]);

  const groupKeys = featureKeys.slice(0, GROUP_COLUMN_COUNT);
  const detailKeys = featureKeys.slice(GROUP_COLUMN_COUNT);

  // Stable "Group N" numbering based on position in the full list, so a
  // group keeps its number even when search filters the visible rows.
  const groupLabelById = useMemo(
    () =>
      new Map(
        combinations.map((combo, index) => [combo.combinationId, `Group ${groupPrefix}${index + 1}`])
      ),
    [combinations, groupPrefix]
  );
  const withGroupLabel = (combo) => ({
    ...combo,
    groupLabel: groupLabelById.get(combo.combinationId) || combo.groupLabel,
  });

  const filteredCombinations = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    if (!term) return combinations;

    return combinations.filter((combo) => {
      const groupText = groupKeys.map((key) => combo.combination[key]).join(" ").toLowerCase();
      const featureText = detailKeys
        .map((key) => `${key} ${combo.combination[key]}`)
        .join(" ")
        .toLowerCase();
      const skuText = combo.skus.map((sku) => `${sku._skuCode} ${sku._description}`).join(" ").toLowerCase();

      return groupText.includes(term) || featureText.includes(term) || skuText.includes(term);
    });
  }, [combinations, debouncedSearch, groupKeys, detailKeys]);

  const totalPages = Math.max(1, Math.ceil(filteredCombinations.length / ITEMS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStart = (safeCurrentPage - 1) * ITEMS_PER_PAGE;
  const pageCombinations = filteredCombinations.slice(pageStart, pageStart + ITEMS_PER_PAGE);

  // Reset to page 1 whenever the search term or the underlying data set changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, combinations]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const selectedCombos = Object.values(selectedCombinationIds || {});
  const selectedSkuTotal = useMemo(
    () => selectedCombos.reduce((sum, combo) => sum + (combo.skuCount || combo.skus?.length || 0), 0),
    [selectedCombos]
  );

  // "Select all" covers every group matching the current search — not just the
  // visible page — so it genuinely means "all groups", matching what's shown
  // in the "Showing X-Y of Z" summary below the table.
  const allFilteredSelected =
    filteredCombinations.length > 0 &&
    filteredCombinations.every((combo) => Boolean(selectedCombinationIds?.[combo.combinationId]));

  const handleToggleAll = () => {
    onToggleAllCombinations(filteredCombinations.map(withGroupLabel), allFilteredSelected);
  };

  return (
    <section className="feature-panel feature-panel--combinations">
      <div className="feature-panel__header feature-panel__header--row">
        <div className="feature-panel__title-wrap">
          <div className="feature-panel__icon">
            <Layers size={20} />
          </div>
          <div>
            <h3>Feature Combinations</h3>
            <p>
              Browse grouped feature combinations instead of filtering SKUs one by
              one — select a group to use its SKUs for forecasting.
            </p>
          </div>
        </div>

        <div className="feature-count">
          <div className="feature-count-label">
            <Sparkles size={14} />
            Total Combinations
          </div>
          <strong>{Number.isFinite(total) ? total.toLocaleString() : "—"}</strong>
        </div>
      </div>

      {selectedCombos.length > 0 && (
        <div className="combination-selection-summary">
          <strong>{selectedCombos.length}</strong>&nbsp;group{selectedCombos.length === 1 ? "" : "s"} selected
          &nbsp;·&nbsp;
          <strong>{selectedSkuTotal}</strong>&nbsp;SKU{selectedSkuTotal === 1 ? "" : "s"}
        </div>
      )}

      {isLoading ? (
        <div className="feature-loader">
          <Loader size="large" />
          <span>Loading feature combinations…</span>
        </div>
      ) : error ? (
        <StatusMessage tone="danger" title="Unable to load feature combinations">
          {error.message || "Please try again."}
        </StatusMessage>
      ) : combinations.length === 0 ? (
        <StatusMessage tone="warning" title="No combinations available">
          This product type did not return any feature combinations.
        </StatusMessage>
      ) : (
        <>
          <div className="combination-toolbar">
            <div className="combination-search">
              <Search size={16} className="combination-search__icon" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search Segment, Collection, Product Category, Features, SKUs…"
              />
              {searchTerm && (
                <button
                  type="button"
                  className="combination-search__clear"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                >
                  <X size={15} />
                </button>
              )}
            </div>
            {isFetching && <span className="combination-toolbar__status">Refreshing…</span>}
          </div>

          {filteredCombinations.length === 0 ? (
            <StatusMessage tone="info" title="No results">
              No combinations match your search. Try a different term.
            </StatusMessage>
          ) : (
            <>
              <div className="sku-table-wrap combination-table-wrap">
                <table className="sku-table combination-table">
                  <thead>
                    <tr>
                      <th className="combination-table__checkbox-col">
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          onChange={handleToggleAll}
                          aria-label="Select all groups"
                        />
                      </th>
                      <th>Group</th>
                      {groupKeys.map((key) => (
                        <th key={key}>{key}</th>
                      ))}
                      <th>Features</th>
                      <th>SKUs</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageCombinations.map((combo, index) => {
                      const isSelected = Boolean(selectedCombinationIds?.[combo.combinationId]);
                      const isExpanded = Boolean(expandedCombinationIds?.[combo.combinationId]);
                      const groupLabel =
                        groupLabelById.get(combo.combinationId) ||
                        `Group ${groupPrefix}${pageStart + index + 1}`;

                      return (
                        <Fragment key={combo.combinationId}>
                          <tr className={isSelected ? "sku-table__row--selected" : ""}>
                            <td className="combination-table__checkbox-col">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => onToggleCombination({ ...combo, groupLabel })}
                                aria-label={`Select ${groupLabel}`}
                              />
                            </td>
                            <td className="combination-table__group-col">{groupLabel}</td>
                            {groupKeys.map((key) => (
                              <td key={key}>{toTitleCase(combo.combination[key]) ?? "-"}</td>
                            ))}
                            <td className="combination-cell--left">
                              <div className="combination-feature-chips">
                                {detailKeys.map((key) => (
                                  <span key={key} className="combination-feature-chip">
                                    {key}: {toTitleCase(combo.combination[key]) ?? "-"}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td>{combo.skuCount}</td>
                            <td>
                              <button
                                type="button"
                                className="combination-details-btn"
                                onClick={() => onToggleDetails(combo.combinationId)}
                              >
                                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                Details
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="combination-details-row">
                              <td colSpan={groupKeys.length + 5}>
                                <table className="combination-sku-detail-table">
                                  <thead>
                                    <tr>
                                      <th>SKU Code</th>
                                      <th>Description</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {combo.skus.map((sku) => (
                                      <tr key={sku._rowId}>
                                        <td>{sku._skuCode}</td>
                                        <td>{sku._description}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="sku-table-meta">
                <span>
                  Showing {pageStart + 1}-{Math.min(pageStart + ITEMS_PER_PAGE, filteredCombinations.length)} of{" "}
                  {filteredCombinations.length} combinations
                  {debouncedSearch ? ` (filtered from ${combinations.length})` : ""}
                </span>
              </div>

              {totalPages > 1 && (
                <nav className="combination-pagination">
                  <button
                    type="button"
                    onClick={() => handlePageChange(safeCurrentPage - 1)}
                    disabled={safeCurrentPage === 1}
                  >
                    Previous
                  </button>

                  {getVisiblePages(safeCurrentPage, totalPages).map((pageNum) => (
                    <button
                      key={pageNum}
                      type="button"
                      className={pageNum === safeCurrentPage ? "is-active" : ""}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  ))}

                  <button
                    type="button"
                    onClick={() => handlePageChange(safeCurrentPage + 1)}
                    disabled={safeCurrentPage === totalPages}
                  >
                    Next
                  </button>
                </nav>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
};

export default FeatureCombinationsTable;
